import { deriveCouponStatus, type Coupon, type CouponGroup, type CouponStatus } from './fixtures';

export function allEntities(coupons: readonly Coupon[], recommendations: readonly Coupon[]): readonly Coupon[] {
  return [...coupons, ...recommendations];
}

export function entityById(entities: readonly Coupon[], id: number | string): Coupon | undefined {
  return entities.find((coupon) => coupon.id === Number(id));
}

function globalTarget(other: Coupon): boolean {
  return other.globalStackingEligible !== false;
}

export function pairSources(aId: number, bId: number, entities: readonly Coupon[], groups: readonly CouponGroup[]): readonly string[] {
  const a = entityById(entities, aId);
  const b = entityById(entities, bId);
  if (!a || !b || a.id === b.id) return [];

  const sources: string[] = [];
  if (a.stackMode === 'global' && globalTarget(b)) sources.push(`${a.name}设置为全局叠加`);
  if (b.stackMode === 'global' && globalTarget(a)) sources.push(`${b.name}设置为全局叠加`);
  if (a.stackMode === 'specified' && a.specified.includes(b.id)) sources.push(`${a.name}指定叠加`);
  if (b.stackMode === 'specified' && b.specified.includes(a.id)) sources.push(`${b.name}指定叠加`);
  groups.forEach((group) => {
    if (group.couponIds.includes(a.id) && group.couponIds.includes(b.id)) sources.push(`组内规则：${group.name}`);
  });
  return [...new Set(sources)];
}

export function relatedCoupons(id: number, entities: readonly Coupon[], groups: readonly CouponGroup[]): readonly Coupon[] {
  return entities.filter((coupon) => coupon.id !== id && pairSources(id, coupon.id, entities, groups).length > 0);
}

export function relationSummary(id: number, entities: readonly Coupon[], groups: readonly CouponGroup[]): string {
  const count = relatedCoupons(id, entities, groups).length;
  return count > 0 ? `可与${count}张优惠券叠加` : '暂无叠加范围';
}

export function hasExternalRange(coupon: Coupon, related: readonly Coupon[], entities: readonly Coupon[], groups: readonly CouponGroup[]): boolean {
  return related.some((other) => pairSources(coupon.id, other.id, entities, groups).some((source) => !source.startsWith(coupon.name)));
}

export interface UnifiedSingleRule {
  readonly entity: Coupon;
  readonly key: string;
  readonly kind: 'single';
  readonly name: string;
  readonly range: string;
  readonly status: CouponStatus;
}

export interface UnifiedGroupRule {
  readonly group: CouponGroup;
  readonly key: string;
  readonly kind: 'group';
  readonly name: string;
  readonly range: string;
  readonly status: '';
}

export type UnifiedRule = UnifiedSingleRule | UnifiedGroupRule;

function rangeText(ids: readonly number[], entities: readonly Coupon[]): string {
  return ids.map((id) => entityById(entities, id)?.name).filter((name): name is string => Boolean(name)).join('、');
}

export function buildUnifiedRules(entities: readonly Coupon[], groups: readonly CouponGroup[]): readonly UnifiedRule[] {
  const singles: UnifiedSingleRule[] = entities
    .filter((coupon) => coupon.stackMode !== 'none')
    .map((coupon) => ({
      entity: coupon,
      key: `single-${coupon.id}`,
      kind: 'single',
      name: coupon.name,
      range: coupon.stackMode === 'global' ? '全部优惠券' : rangeText(coupon.specified, entities),
      status: deriveCouponStatus(coupon),
    }));

  const groupRules: UnifiedGroupRule[] = groups.map((group) => ({
    group,
    key: `group-${group.id}`,
    kind: 'group',
    name: group.name,
    range: rangeText(group.couponIds, entities),
    status: '',
  }));

  return [...singles, ...groupRules];
}
